import { IconExit } from '@consta/icons/IconExit'
import { IconUser } from '@consta/icons/IconUser'
import { Button } from '@consta/uikit/Button'
import { Popover } from '@consta/uikit/Popover'
import { Text } from '@consta/uikit/Text'
import { User } from '@consta/uikit/User'
import {
  selectDisplayName,
  selectUserName,
  setUserInfo,
  startConnectingGeoImportWorks,
  startConnectingML,
  startConnectingProgress,
  startConnectingProjects,
  startConnectingSeismicImportProgress,
  startConnectingTreeObjects,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { AppHeaderProps } from '@core/core/types'
import { Delimiter, Header, Logo, ThemeSwitch } from '@library/components'
import { useMetaChange } from '@library/hooks'
import { parseToken, removeToken } from '@library/token'
import type { Nullable } from 'common/types'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'

import css from './AppHeader.css'

export const AppHeader: FC<AppHeaderProps> = ({ logout, getToken }) => {
  const userInfoRef = useRef<Nullable<HTMLDivElement>>(null)
  const [showUserPopover, setShowUserPopover] = useState<boolean>(false)

  const dispatch = useAppDispatch()

  const displayName = useAppSelector(selectDisplayName)
  const userName = useAppSelector(selectUserName)

  const rawToken = getToken()

  useEffect(() => {
    if (!rawToken) return

    const token = parseToken(rawToken)
    dispatch(setUserInfo(token))
  }, [rawToken])

  useEffect(() => {
    if (!rawToken) return

    dispatch(startConnectingProjects())
    dispatch(startConnectingTreeObjects())
    dispatch(startConnectingML())
    dispatch(startConnectingProgress())
    dispatch(startConnectingSeismicImportProgress())
    dispatch(startConnectingGeoImportWorks())
  }, [rawToken])

  const onUserInfoClick = () => {
    setShowUserPopover(prevState => !prevState)
  }

  const onExit = () => {
    for (const localStorageKey in localStorage) {
      if (localStorageKey.includes('kc-callback')) {
        localStorage.removeItem(localStorageKey)
      }
    }
    dispatch(setUserInfo(null))
    removeToken()
    localStorage.removeItem('company')
    logout()
  }

  useMetaChange('Геомейт WEB')

  if (!rawToken) {
    return null
  }

  return (
    <Header className={css.header}>
      <Header.LeftBlock>
        <Logo />
      </Header.LeftBlock>
      <Header.RightBlock>
        <>
          <ThemeSwitch />
          <div ref={userInfoRef}>
            <User
              data-testid="user-avatar"
              withArrow={true}
              name={userName}
              onlyAvatar={true}
              onClick={onUserInfoClick}
            />
            {!!showUserPopover && (
              <Popover
                anchorRef={userInfoRef}
                direction="downStartRight"
                offset="m"
                onClickOutside={() => {
                  setShowUserPopover(false)
                }}>
                <div className={css.userPopover}>
                  <div className={css.popoverItem}>
                    <IconUser size="xs" view="primary" />
                    <Text data-testid="user-name" size="s" view="primary">
                      {displayName}
                    </Text>
                  </div>
                  <Delimiter className={css.delimiter} position="horizontal" />
                  <Button
                    className={`${css.popoverItem} ${css.exitButton}`}
                    size="s"
                    view="clear"
                    width="full"
                    label="Выйти"
                    iconLeft={IconExit}
                    iconSize="s"
                    onClick={onExit}
                  />
                  <Text className={`${css.popoverItem} ${css.version}`} size="2xs" view="ghost">
                    {/* eslint-disable-next-line no-underscore-dangle */}
                    ver {window._env_?.VERSION ?? 'no ver info'}
                  </Text>
                </div>
              </Popover>
            )}
          </div>
        </>
      </Header.RightBlock>
    </Header>
  )
}
